import { useOrganization } from '@clerk/clerk-react'
import { ResponseError, type TenantResponseDto } from '@saaga/billingv2-sdk'
import {
  clearBilling,
  selectBilling,
  setBillingError,
  setBillingLoading,
  setTenant,
} from '@saaga/shared/store/slices/billing.slice'
import type { AppDispatch } from '@saaga/shared/store/store'
import { useAppDispatch, useAppSelector } from '@saaga/shared/store/store'
import {
  BillingErrorCode,
  createBillingError,
  getBillingErrorMessage,
  transformTenantResponse,
} from '@saaga/shared/utils/billing.util'
import { useCallback, useEffect, useRef } from 'react'

import { useBillingClient } from './use-billing-client.hook'

interface BillingSyncResult {
  isLoading: boolean
  error: string | null
  refetch: () => void
}

const isTenantNotFoundError = (error: unknown): boolean =>
  error instanceof ResponseError && error.response.status === 404

const parseBillingError = (error: unknown): string => {
  if (isTenantNotFoundError(error)) {
    return getBillingErrorMessage(
      createBillingError(BillingErrorCode.tenantNotFound, 'Tenant not found')
    )
  }

  if (error instanceof ResponseError) {
    return getBillingErrorMessage(
      createBillingError(BillingErrorCode.networkError, error.message, error)
    )
  }

  if (error instanceof Error) {
    return error.message
  }

  return getBillingErrorMessage(createBillingError(BillingErrorCode.unknownError, 'Unknown error'))
}

const loadTenantAndBalance = async (
  fetchFn: () => Promise<TenantResponseDto>,
  dispatch: AppDispatch,
  refetchBalance: () => Promise<void>
) => {
  const tenantResponse = await fetchFn()
  dispatch(setTenant(transformTenantResponse(tenantResponse)))
  await refetchBalance()
}

export const useBillingSync = (): BillingSyncResult => {
  const dispatch = useAppDispatch()
  const { organization } = useOrganization()
  const billingState = useAppSelector(selectBilling)
  const { getCurrentTenant, syncTenant, refetchBalance } = useBillingClient()

  const previousOrgIdRef = useRef<string | null>(null)
  const isFetchingRef = useRef(false)

  const fetchBilling = useCallback(async () => {
    if (!organization?.id) {
      dispatch(clearBilling())
      return
    }

    if (isFetchingRef.current) {
      return
    }

    isFetchingRef.current = true
    dispatch(setBillingLoading())

    try {
      await loadTenantAndBalance(getCurrentTenant, dispatch, refetchBalance)
    } catch (error) {
      if (isTenantNotFoundError(error)) {
        try {
          await loadTenantAndBalance(syncTenant, dispatch, refetchBalance)
        } catch (createError) {
          dispatch(setBillingError(parseBillingError(createError)))
        }
        return
      }
      dispatch(setBillingError(parseBillingError(error)))
    } finally {
      isFetchingRef.current = false
    }
  }, [organization?.id, getCurrentTenant, syncTenant, refetchBalance, dispatch])

  useEffect(() => {
    if (organization?.id !== previousOrgIdRef.current) {
      previousOrgIdRef.current = organization?.id ?? null
      fetchBilling()
    }
  }, [organization?.id, fetchBilling])

  return {
    isLoading: !billingState.isLoaded,
    error: billingState.error,
    refetch: fetchBilling,
  }
}
